/**
 * A guided tour over the page's own elements (Feature 72 D6, D12): the page
 * dims, a highlight rings the step's element, found by its `data-tour`
 * attribute, and a card beside it says what the element does, with Back,
 * Next (Done on the last step), and Close. A step whose element is not on the
 * page shows its card in the middle with nothing ringed.
 *
 * The tour does not block the page: the dimming lets clicks through, so the
 * operator can try what a step describes. The highlight follows its element
 * frame by frame, since a canvas pans and a panel scrolls under it. Escape
 * closes; focus moves into the card and goes back where it was after.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { Button } from './Button.js';
import { IconButton, Inline, Typography } from './finixt.js';

// Above the page and the record panel, below menus (86), so a step's own
// dropdown still opens over it.
const LAYER_Z_INDEX = 84;
const CARD_WIDTH = 320;
// The card's height before it is measured.
const CARD_HEIGHT = 180;
const MARGIN = 16;
const GAP = 13;
// How far the highlight stands off its element.
const RING = 5;

export interface TourStep {
  /** The `data-tour` value of the element the step points at. */
  target: string;
  title: string;
  body: ReactNode;
  /** Runs as the step opens, before its element is looked for: to select what it shows. */
  onEnter?: (() => void) | undefined;
}

export interface TourProps {
  /** Names the tour on its card, as "How it works · 2 of 6". */
  label: string;
  steps: readonly TourStep[];
  /** Opening it starts from the first step. */
  open: boolean;
  /** Closed, or finished with Done when `finished` is true. */
  onClose: (finished: boolean) => void;
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where the card goes beside its element. An element no taller than the card,
 * a control or a marker, takes it below, then above, then to the right or
 * the left, so the card covers what is under the element rather than what
 * follows it; a taller one, a panel, takes it to the right or the left first.
 * When no side has room, as for a canvas that fills the page, the card goes
 * inside the element's lower right corner. It is then kept inside the viewport.
 */
export function placeCard(
  target: Box,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
): { x: number; y: number } {
  const right = { x: target.x + target.width + GAP, y: target.y };
  const left = { x: target.x - GAP - card.width, y: target.y };
  const below = { x: target.x, y: target.y + target.height + GAP };
  const above = { x: target.x, y: target.y - GAP - card.height };
  const fits = {
    right: right.x + card.width <= viewport.width - MARGIN,
    left: left.x >= MARGIN,
    below: below.y + card.height <= viewport.height - MARGIN,
    above: above.y >= MARGIN,
  };
  const sides: (keyof typeof fits)[] =
    target.height <= card.height
      ? ['below', 'above', 'right', 'left']
      : ['right', 'left', 'below', 'above'];
  const place = { right, left, below, above };
  const side = sides.find((s) => fits[s]);
  const at = side
    ? place[side]
    : {
        x: target.x + target.width - card.width - MARGIN,
        y: target.y + target.height - card.height - MARGIN,
      };
  return {
    x: Math.max(MARGIN, Math.min(at.x, viewport.width - card.width - MARGIN)),
    y: Math.max(MARGIN, Math.min(at.y, viewport.height - card.height - MARGIN)),
  };
}

function boxOf(target: string): Box | null {
  const el = document.querySelector(`[data-tour="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
}

function sameBox(a: Box | null, b: Box | null): boolean {
  if (a === null || b === null) return a === b;
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

export function Tour({ label, steps, open, onClose }: TourProps) {
  const [index, setIndex] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const [cardHeight, setCardHeight] = useState(CARD_HEIGHT);
  const card = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const step = steps[index];
  // A page may rebuild its steps on every render; the effects follow the
  // index, and read the step that is current when they run.
  const current = useRef(step);
  current.current = step;

  // Every opening starts from the first step and remembers where focus was.
  useLayoutEffect(() => {
    if (!open) return;
    opener.current = document.activeElement as HTMLElement | null;
    setIndex(0);
    return () => opener.current?.focus();
  }, [open]);

  // A step selects what it shows first, then its element is brought into view.
  useEffect(() => {
    if (!open) return;
    current.current?.onEnter?.();
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-tour="${current.current?.target ?? ''}"]`)
        ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      card.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open, index]);

  // The highlight follows its element, which may move under a pan or a scroll.
  useEffect(() => {
    if (!open) return;
    let frame = 0;
    const track = () => {
      const next = current.current ? boxOf(current.current.target) : null;
      setBox((prev) => (sameBox(prev, next) ? prev : next));
      const height = card.current?.offsetHeight;
      if (height) setCardHeight((prev) => (prev === height ? prev : height));
      frame = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(frame);
  }, [open, index]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !step) return null;
  const last = index === steps.length - 1;
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const at = box
    ? placeCard(box, { width: CARD_WIDTH, height: cardHeight }, viewport)
    : {
        x: (viewport.width - CARD_WIDTH) / 2,
        y: Math.max(MARGIN, (viewport.height - cardHeight) / 2),
      };

  return createPortal(
    <div
      data-testid="tour"
      style={{ position: 'fixed', inset: 0, zIndex: LAYER_Z_INDEX, pointerEvents: 'none' }}
    >
      {box ? (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: box.x - RING,
            top: box.y - RING,
            width: box.width + RING * 2,
            height: box.height + RING * 2,
            borderRadius: 'var(--radius-card)',
            border: '2px solid var(--blue)',
            boxShadow: '0 0 0 9999px var(--scrim)',
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, background: 'var(--scrim)' }}
        />
      )}
      <div
        ref={card}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          position: 'fixed',
          left: at.x,
          top: at.y,
          width: CARD_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          padding: 16,
          pointerEvents: 'auto',
          background: 'var(--surface-card)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius-card)',
          boxShadow: 'var(--shadow-popover)',
          outline: 'none',
        }}
      >
        <Inline align="center" gap={7} wrap={false}>
          <Typography variant="eyebrow" style={{ flex: 1 }}>
            {label} · <span className="mono">{index + 1}</span> of{' '}
            <span className="mono">{steps.length}</span>
          </Typography>
          <IconButton
            icon="close"
            size={28}
            tone="bare"
            aria-label="Close the tour"
            onClick={() => onClose(false)}
          />
        </Inline>
        <Typography variant="card" id={titleId}>
          {step.title}
        </Typography>
        <Typography variant="body-muted">{step.body}</Typography>
        <Inline align="center" justify="flex-end" gap={7} wrap={false}>
          <Button
            size="sm"
            variant="ghost"
            disabled={index === 0}
            onClick={() => setIndex((i) => i - 1)}
          >
            Back
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => (last ? onClose(true) : setIndex((i) => i + 1))}
          >
            {last ? 'Done' : 'Next'}
          </Button>
        </Inline>
      </div>
    </div>,
    document.body,
  );
}
